import { useEffect, useState } from 'react'
import { getStatus } from '../lib/api'
import { languageFromHost } from '../lib/languages'
import { useStore } from './store'

export interface Access {
  loading: boolean
  signedIn: boolean
  isAdmin: boolean
  /** Languages this account holds a subscription for. */
  languages: string[]
  /** Whether this site's language is one of them. */
  entitledHere: boolean
  /** Whether a free trial of this language is still on offer. */
  trialAvailable: boolean
  /**
   * How this site's language is held: a trial that ends on a date, a Stripe
   * subscription, a comp, or an administrator's grant.
   *
   * Separate from entitledHere because the two answer different questions, and
   * conflating them cost real money: a trialling account is entitled, so it
   * never saw the paywall, and the paywall was the only screen with a subscribe
   * button on it.
   */
  standing: { source: string; expiresAt: number | null } | null
}

/**
 * What this account is allowed to do, as the server sees it.
 *
 * The pills, the admin entry and the paywall all read from here so they cannot
 * disagree. None of it is the enforcement — every data route re-checks
 * server-side and answers 402 — but the interface should not offer what the
 * server will refuse.
 */
export function useAccess(): Access {
  const { data } = useStore()
  const token = data.sync.token
  const [access, setAccess] = useState<Access>({
    loading: true,
    signedIn: false,
    isAdmin: false,
    languages: [],
    entitledHere: false,
    trialAvailable: false,
    standing: null,
  })

  useEffect(() => {
    let cancelled = false
    if (!token) {
      setAccess({
        loading: false,
        signedIn: false,
        isAdmin: false,
        languages: [],
        entitledHere: false,
        trialAvailable: false,
        standing: null,
      })
      return
    }

    void getStatus(token).then((result) => {
      if (cancelled) return
      if (!result.ok) {
        // Offline: assume the site you are on is fine rather than throwing up a
        // paywall over a dropped connection. The server still refuses on sync.
        setAccess({
          loading: false,
          signedIn: true,
          isAdmin: false,
          languages: languageFromHost() ? [languageFromHost() as string] : [],
          entitledHere: true,
          trialAvailable: false,
          // Offline. Saying nothing beats guessing at a billing state, since
          // every branch of that card is a claim about somebody's money.
          standing: null,
        })
        return
      }
      setAccess({
        loading: false,
        signedIn: result.signedIn,
        isAdmin: result.isAdmin,
        languages: result.languages,
        entitledHere: result.entitledHere,
        trialAvailable: result.trialAvailable,
        standing: result.standing ?? null,
      })
    })

    return () => {
      cancelled = true
    }
  }, [token])

  return access
}
